<template>
  <b-modal
    :visible="visible"
    title="Enviar Cartão de Aniversário"
    size="md"
    title-class="modal-title-custom"
    header-class="modal-header-custom"
    body-class="modal-body-custom p-0"
    footer-class="modal-footer-custom"
    @change="$emit('update:visible', $event)"
    centered
    no-close-on-backdrop
  >
    <div class="birthday-card-preview">
      <div class="card-blue-accent"></div>
      
      <div class="card-content">
        <h1 class="card-title">
          Feliz Aniversário,<br />{{ (contact && contact.nome) ? contact.nome : 'Cliente' }}!
        </h1>
        
        <div class="message-editor-wrapper">
          <b-form-textarea
            v-model="internalMessage"
            class="message-editor"
            rows="4"
            placeholder="Digite a mensagem de aniversário..."
          ></b-form-textarea>
        </div>
        
        <p class="card-signoff">Um abraço,</p>
        <p class="card-sender">{{ senderName }}</p>
        
        <div class="card-footer">
          <span class="company-name">SalesFlow</span>
        </div>
      </div>
    </div>

    <template #modal-footer>
      <div class="d-flex justify-content-end w-100 p-3 pt-0 gap-2">
        <b-button variant="outline-secondary" @click="$emit('update:visible', false)" class="btn-cancel">
          Cancelar
        </b-button>
        <b-button variant="primary" class="btn-send-blue" @click="sendCard" :disabled="sending">
          <b-spinner v-if="sending" small class="mr-1"></b-spinner>
          <feather-icon v-else icon="SendIcon" size="14" class="mr-1" />
          Enviar Cartão
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import {
  BModal, BButton, BFormTextarea, BSpinner
} from 'bootstrap-vue';
import { getUserData } from '@/auth/utils';
import crmClient from '@/api/CRM/crm.client';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'CartaoAniversario',
  components: {
    BModal, BButton, BFormTextarea, BSpinner
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    contact: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      internalMessage: 'Desejamos a você muita alegria, sucesso e realizações neste dia tão especial. Que o seu novo ano traga grandes conquistas e muitas felicidades!',
      sending: false,
    };
  },
  computed: {
    currentUser() {
      return getUserData();
    },
    senderName() {
      return this.currentUser?.nome || this.currentUser?.nomeFantasia || 'Nossa Equipe';
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // Reset message when opened
        this.internalMessage = 'Desejamos a você muita alegria, sucesso e realizações neste dia tão especial. Que o seu novo ano traga grandes conquistas e muitas felicidades!';
      }
    }
  },
  methods: {
    async sendCard() {
      if (!this.contact?.email) {
         this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Ops!',
            text: 'Este contato não possui um e-mail cadastrado.',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        });
        return;
      }

      this.sending = true;
      try {
        const payload = {
          destinatarioEmail: this.contact.email,
          destinatarioNome: this.contact.nome,
          remetenteNome: this.senderName,
          mensagem: this.internalMessage,
          nomeEmpresa: 'SalesFlow',
        };

        await crmClient.enviarCartaoAniversario(payload);
        
        // Mark as sent in local storage to prevent duplicate UI clicks
        const year = new Date().getFullYear();
        const key = `birthday_sent_${this.contact.clienteiderp || this.contact.email}_${year}`;
        localStorage.setItem(key, 'true');

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Cartão Enviado!',
            text: `Cartão enviado com sucesso para ${this.contact.nome}`,
            icon: 'CheckIcon',
            variant: 'success',
          },
        });

        this.$emit('sent', this.contact);
        this.$emit('update:visible', false);

      } catch (error) {
        console.error('Erro ao enviar cartão:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro no Envio',
            text: error.response?.data?.message || 'Falha ao enviar o cartão. Tente novamente.',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.sending = false;
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&display=swap');

/* Premium B2B Modal Overrides */
.modal-header-custom {
  background: var(--page-bg, #f8fafc) !important;
  border-bottom: 1px solid var(--border-color, #e2e8f0) !important;
  padding: 1.25rem 1.5rem !important;
}

.modal-title-custom {
  font-weight: 700 !important;
  color: var(--text-heading, #1e293b) !important;
  font-size: 1.15rem !important;
}

.modal-footer-custom {
  background: var(--page-bg, #f8fafc) !important;
  border-top: none !important;
  padding: 0 !important;
}

/* Birthday Card Design */
.birthday-card-preview {
  background-color: #010626; /* Deep Navy */
  color: #ffffff;
  min-height: 400px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 2rem 1.5rem;
  overflow: hidden;
}

.card-blue-accent {
  background: linear-gradient(135deg, #097bc0, #03b6e2); /* Blue to Cyan */
  height: 4px;
  width: 70px;
  margin-bottom: 2rem;
}

.card-content {
  width: 100%;
  max-width: 400px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 2;
}

.card-title {
  font-family: 'Playfair Display', Georgia, serif;
  font-weight: 700;
  font-size: 2rem;
  color: #00bfff !important; /* Bright Cyan for better contrast */
  line-height: 1.2;
  margin-bottom: 1.5rem;
  text-shadow: 0 2px 10px rgba(0, 191, 255, 0.4);
}

.message-editor-wrapper {
  width: 100%;
  margin-bottom: 1.5rem;
  position: relative;
}

.message-editor-wrapper::before,
.message-editor-wrapper::after {
  content: '"';
  position: absolute;
  font-family: 'Playfair Display', Georgia, serif;
  font-size: 2rem;
  color: rgba(3, 182, 226, 0.5) !important; /* Cyan shadow */
}

.message-editor-wrapper::before {
  top: -15px;
  left: -20px;
}

.message-editor-wrapper::after {
  bottom: -30px;
  right: -20px;
}

.message-editor {
  background: rgba(255, 255, 255, 0.03) !important;
  border: 1px dashed rgba(3, 182, 226, 0.3) !important;
  color: #ffffff !important;
  font-size: 1rem !important;
  font-style: italic;
  font-family: 'Playfair Display', Georgia, serif;
  text-align: center;
  padding: 0.75rem !important;
  resize: none;
  border-radius: 8px;
  transition: all 0.3s ease;
  line-height: 1.5;
}

.message-editor:focus {
  background: rgba(255, 255, 255, 0.05) !important;
  border-color: #84d8f0 !important;
  box-shadow: 0 0 0 4px rgba(132, 216, 240, 0.1) !important;
  color: #ffffff !important;
}

.message-editor::placeholder {
  color: rgba(209, 213, 219, 0.4);
}

.card-signoff {
  font-size: 0.95rem;
  color: #e5e7eb !important;
  margin-bottom: 0.25rem;
}

.card-sender {
  font-size: 1.1rem;
  font-weight: 600;
  color: #00bfff !important; /* Bright Cyan */
  margin-top: 0;
  margin-bottom: 2rem;
}

.card-footer {
  width: 100%;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.company-name {
  font-size: 0.85rem;
  font-weight: 400;
  letter-spacing: 2px;
  color: #ffffff !important;
  text-transform: uppercase;
}

/* Blue Button */
.btn-send-blue {
  background: linear-gradient(135deg, #097bc0, #03b6e2) !important;
  border: none !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  transition: all 0.2s ease;
}

.btn-send-blue:hover:not(:disabled) {
  background: linear-gradient(135deg, #03b6e2, #84d8f0) !important;
  color: #010626 !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(3, 182, 226, 0.3);
}

.btn-cancel {
  font-weight: 600;
  border-color: #cbd5e1;
  color: #475569;
}

.btn-cancel:hover {
  background: #f1f5f9;
  color: #1e293b;
}

/* Dark layout overrides just in case (though card is always dark) */
body.dark-layout .modal-header-custom,
body.dark-layout .modal-footer-custom {
  background: #1e293b !important;
  border-color: #334155 !important;
}

body.dark-layout .modal-title-custom {
  color: #f8fafc !important;
}
</style>
